import { formatAmount } from "../../utils/FormatPerson";

export type UserWalletRecord = {
  _id?: string;
  address?: string;
  currency?: string;
  network?: string;
  walletType?: string;
  entity?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type WalletSegment = "otc" | "sell" | "deposit";

const NETWORK_LABELS: Record<string, string> = {
  BTC: "Bitcoin",
  ETH: "ERC20",
  BSC: "BEP20",
  TRX: "TRC20",
  SOL: "Solana",
  DOGE: "Dogecoin",
};

export function networkDisplayLabel(network: string | undefined): string {
  if (!network) return "—";
  const upper = network.toUpperCase();
  return NETWORK_LABELS[upper] ?? upper;
}

export function truncateAddress(address: string, head = 8, tail = 8): string {
  if (address.length <= head + tail + 3) return address;
  return `${address.slice(0, head)}...${address.slice(-tail)}`;
}

function walletKey(w: UserWalletRecord): string {
  return [
    w.currency ?? "",
    w.network ?? "",
    w.address ?? "",
    w.walletType ?? "",
    w.entity ?? "USER",
  ].join("|");
}

export function dedupeWallets(wallets: UserWalletRecord[]): UserWalletRecord[] {
  const seen = new Map<string, UserWalletRecord>();
  for (const wallet of wallets) {
    const key = walletKey(wallet);
    const existing = seen.get(key);
    if (!existing) {
      seen.set(key, wallet);
      continue;
    }
    const existingTime = new Date(existing.updatedAt ?? existing.createdAt ?? 0);
    const nextTime = new Date(wallet.updatedAt ?? wallet.createdAt ?? 0);
    if (nextTime >= existingTime) seen.set(key, wallet);
  }
  return Array.from(seen.values());
}

export function filterWalletsBySegment(
  wallets: UserWalletRecord[],
  segment: WalletSegment
): UserWalletRecord[] {
  const deduped = dedupeWallets(wallets);
  switch (segment) {
    case "otc":
      return deduped.filter(
        (w) => String(w.entity ?? "").toUpperCase() === "OTC"
      );
    case "sell":
      return deduped.filter(
        (w) =>
          String(w.walletType ?? "").toUpperCase() === "SELL" &&
          String(w.entity ?? "").toUpperCase() !== "OTC"
      );
    case "deposit":
      return deduped.filter(
        (w) => String(w.walletType ?? "").toUpperCase() === "DEPOSIT"
      );
    default:
      return deduped;
  }
}

export function segmentCounts(wallets: UserWalletRecord[]) {
  return {
    otc: filterWalletsBySegment(wallets, "otc").length,
    sell: filterWalletsBySegment(wallets, "sell").length,
    deposit: filterWalletsBySegment(wallets, "deposit").length,
  };
}

export function resolveWalletBalance(
  currency: string | undefined,
  balances: Record<string, unknown> | undefined
): number | undefined {
  if (!currency || !balances) return undefined;
  const key = currency.toLowerCase();
  const flat = balances[key];
  if (typeof flat === "number" && !Number.isNaN(flat)) return flat;
  return undefined;
}

export function formatWalletBalance(
  currency: string | undefined,
  balances: Record<string, unknown> | undefined
): string {
  const amount = resolveWalletBalance(currency, balances);
  if (amount == null) return "—";
  const key = (currency ?? "").toLowerCase();
  const symbol = currency?.toUpperCase() ?? "";
  if (key === "ngn") return `₦ ${formatAmount(amount)}`;
  return `${formatAmount(amount)} ${symbol}`.trim();
}

export function sortWallets(wallets: UserWalletRecord[]): UserWalletRecord[] {
  return [...wallets].sort((a, b) => {
    const currencyCmp = (a.currency ?? "").localeCompare(b.currency ?? "");
    if (currencyCmp !== 0) return currencyCmp;
    return (a.network ?? "").localeCompare(b.network ?? "");
  });
}
